import React from 'react'
import { Card, CardContent, Typography, Chip, Box, Grid, Divider, IconButton, Collapse, Button } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer'
import ReplyIcon from '@mui/icons-material/Reply'
import DeleteIcon from '@mui/icons-material/Delete'
import PriorityHighIcon from '@mui/icons-material/PriorityHigh'

interface QnaListComponentProps {
    onDeleteClick: () => void;
    onReplyClick: () => void;
    _id: string;
    qnaTitle: string;
    qnaQuestion: string;
    qnaReplyCount: number;
    qnaCreatedAt: number;
    qnaUpdatedAt: number;
    replyEnabled: boolean;
    qnaReplies: [];
    isDeleted: boolean;
    isPriority: boolean;
}

export default function QnaListComponent({
    onDeleteClick,
    onReplyClick,
    _id,
    qnaTitle,
    qnaQuestion,
    qnaReplyCount,
    qnaCreatedAt,
    qnaUpdatedAt,
    replyEnabled,
    qnaReplies,
    isDeleted,
    isPriority,
}: QnaListComponentProps) {
    const [expanded, setExpanded] = React.useState(false);

    const returnDate = (date: number) => {
        const newDate = new Date(date * 1000);
        return newDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleExpandClick = () => {
        setExpanded(!expanded);
    };

    return (
        <Card
            sx={{
                minWidth: 600,
                width: 700,
                margin: '16px auto',
                boxShadow: isDeleted ? '0 4px 20px rgba(239, 68, 68, 0.3)' : '0 4px 20px rgba(0,0,0,0.1)',
                border: isDeleted ? '2px solid #ef4444' : isPriority ? '2px solid #f59e0b' : '1px solid #e5e7eb',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                }
            }}
        >
            {/* Header with Status Badges */}
            <Box sx={{ position: 'relative', p: 2, pb: 1 }}>
                <Box sx={{ position: 'absolute', top: 16, right: 16, display: 'flex', gap: 1, flexDirection: 'column' }}>
                    {isPriority && (
                        <Chip
                            icon={<PriorityHighIcon />}
                            label="Priority"
                            size="small"
                            sx={{
                                backgroundColor: '#f59e0b',
                                color: 'white',
                                fontWeight: 'bold'
                            }}
                        />
                    )}
                    {isDeleted && (
                        <Chip
                            label="Deleted"
                            size="small"
                            sx={{
                                backgroundColor: '#ef4444',
                                color: 'white',
                                fontWeight: 'bold'
                            }}
                        />
                    )}
                    {!replyEnabled && (
                        <Chip
                            label="Reply Disabled"
                            size="small"
                            sx={{
                                backgroundColor: '#6b7280',
                                color: 'white',
                                fontWeight: 'bold'
                            }}
                        />
                    )}
                </Box>

                {/* Title */}
                <Typography variant="h5" component="h2" gutterBottom sx={{ 
                    fontWeight: 'bold', 
                    color: isDeleted ? '#ef4444' : 'inherit',
                    pr: 15 // Padding right to avoid overlap with badges
                }}>
                    <QuestionAnswerIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    {qnaTitle}
                </Typography>
            </Box>

            <CardContent sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                pt: 0
            }}>
                {/* Main Content Area */}
                <Box sx={{ flex: 1 }}>
                    {/* Stats Row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ReplyIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                                {qnaReplyCount} replies
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AccessTimeIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                                {returnDate(qnaCreatedAt)}
                            </Typography>
                        </Box>
                        {qnaUpdatedAt !== qnaCreatedAt && (
                            <Typography variant="body2" color="text.secondary">
                                Updated: {returnDate(qnaUpdatedAt)}
                            </Typography>
                        )}
                    </Box>

                    {/* Question Preview */}
                    <Typography variant="body1" sx={{
                        mb: 2,
                        lineHeight: 1.6,
                        minHeight: '60px',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        backgroundColor: '#f8fafc',
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0'
                    }}>
                        <strong>Question:</strong> {qnaQuestion.length > 150 ? `${qnaQuestion.substring(0, 150)}...` : qnaQuestion}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    {/* Replies Preview */}
                    <Box sx={{ mb: 2, minHeight: '60px' }}>
                        {qnaReplies && qnaReplies.length > 0 ? (
                            <>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <ReplyIcon fontSize="small" color="primary" />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                        Replies ({qnaReplies.length})
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {qnaReplies.slice(0, 2).map((reply: any, index: number) => (
                                        <Box key={reply._id || index} sx={{
                                            backgroundColor: '#f1f5f9',
                                            padding: '8px 12px',
                                            borderRadius: '8px',
                                            border: '1px solid #cbd5e1'
                                        }}>
                                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                                {reply.qnaReplyMessage.length > 100 ? 
                                                    `${reply.qnaReplyMessage.substring(0, 100)}...` : 
                                                    reply.qnaReplyMessage
                                                }
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {returnDate(reply.qnaReplyCreatedAt)}
                                            </Typography>
                                        </Box>
                                    ))}
                                    {qnaReplies.length > 2 && (
                                        <Chip
                                            label={`+${qnaReplies.length - 2} more replies`}
                                            size="small"
                                            variant="outlined"
                                        />
                                    )}
                                </Box>
                            </>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No replies yet
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Action Buttons */}
                {!isDeleted && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<ReplyIcon />}
                            onClick={onReplyClick}
                            disabled={!replyEnabled}
                            sx={{
                                borderRadius: '20px',
                                textTransform: 'none',
                                fontWeight: 'bold'
                            }}
                        >
                            Reply
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={onDeleteClick}
                            sx={{
                                borderRadius: '20px',
                                textTransform: 'none',
                                fontWeight: 'bold'
                            }}
                        >
                            Delete
                        </Button>
                    </Box>
                )}

                {/* Bottom Section - Expand Button */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <IconButton
                        onClick={handleExpandClick}
                        aria-expanded={expanded}
                        aria-label="show more"
                        sx={{
                            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s'
                        }}
                    >
                        <ExpandMoreIcon />
                    </IconButton>
                </Box>

                {/* Expanded Content */}
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ pt: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Detailed Information
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Question ID:</strong> {_id}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Reply Status:</strong> {replyEnabled ? 'Enabled' : 'Disabled'}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Status:</strong> {isDeleted ? 'Deleted' : 'Active'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Priority:</strong> {isPriority ? 'High' : 'Normal'}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Reply Count:</strong> {qnaReplyCount}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Last Updated:</strong> {returnDate(qnaUpdatedAt)}
                                </Typography>
                            </Grid>
                        </Grid>

                        {/* Full Question */}
                        {qnaQuestion.length > 150 && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    Full Question
                                </Typography>
                                <Typography variant="body2" sx={{
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: 1.6,
                                    backgroundColor: '#f8fafc',
                                    padding: '16px',
                                    borderRadius: '8px',
                                    border: '1px solid #e2e8f0'
                                }}>
                                    {qnaQuestion}
                                </Typography>
                            </Box>
                        )}

                        {/* All Replies (Expanded View) */}
                        {qnaReplies && qnaReplies.length > 2 && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    All Replies
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {qnaReplies.map((reply: any, index: number) => (
                                        <Box key={reply._id || index} sx={{
                                            backgroundColor: '#f8fafc',
                                            padding: '16px',
                                            borderRadius: '8px',
                                            border: '1px solid #e2e8f0'
                                        }}>
                                            <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6 }}>
                                                {reply.qnaReplyMessage}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Replied: {returnDate(reply.qnaReplyCreatedAt)}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Collapse>
            </CardContent>
        </Card>
    );
}
